import axios from 'axios';
import { HttpError } from 'react-admin';

axios.defaults.withCredentials = true;

const callApi = async (method, params) => {
	try {
		const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/${method}`, params);

		if(!res.data) {
			throw new HttpError('Missing data from response', res.status);
		}
		
		return res.data;
	} catch(error) {
		if (error.response) {
			throw new HttpError(error.message, error.response.status, error.response.data);
		} else {
			throw new HttpError(error.message, 0);
		}
	}
};

const uploadFile = (method, params, config = {}) => {
	const controller = new AbortController();

	return  [
		axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/${method}`, params, {
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'multipart/form-data'
			},
			signal: controller.signal,
			...config
		}),
		controller
	];
};

const extractFiles = (resource, params) => {
	const files = [];
	if(resource === 'places' && Array.isArray(params.data.photos)) {
		//We may modify the array, so iterator must be converted to array
		let itemsToRemove = [];
		for(const [i, photo] of params.data.photos.entries()) {
			if(photo.file) {
				//File should be uploaded
				const { file, photo_type_id, caption, uid } = photo;
				files.push({
					method: 'uploadPlacePhoto',
					idKey: 'place_id',
					data: {
						file,
						photo_type_id,
						caption,
						ordering: i+1
					},
				});
				itemsToRemove.push(uid);
			}
		}

		params.data.photos = params.data.photos.filter(file => !itemsToRemove.includes(file.uid));
	}

	if(resource === 'places' && Array.isArray(params.data.models)) {
		//We may modify the array, so iterator must be converted to array
		for(const [i, modelData] of params.data.models.entries()) {
			if(modelData.model && modelData.model.rawFile) {
				//File should be uploaded
				files.push({
					method: 'uploadPlaceModel',
					idKey: 'place_id',
					data: {
						model_file: modelData.model.rawFile,
						preview_file: modelData.preview.rawFile,
						model_type_id: modelData.model_type_id,
						caption: typeof modelData.caption != 'undefined' ? modelData.caption : '',
						ordering: i+1
					},
				});
			}
		}

		params.data.models = params.data.models.filter(modelData => !modelData.model || !modelData.model.rawFile);
	}

	return {
		files,
		safeParams: params,
	};
}

const processFiles = (files, response) => {
	return Promise.all(files.map(fileData => {
		const form = new FormData();
		form.append(fileData.idKey, response.data.id);
		for(const [key, val] of Object.entries(fileData.data)) {
			form.append(key, val);
		}

		let [axios, controller] = uploadFile(fileData.method, form);
		return axios;
	}));
}

const dataProvider = {
	getList:    (resource, params) => callApi('list', { resource, ...params }),
	getOne:     (resource, params) => callApi('get', { resource, ...params }),
	getMany:    (resource, params) => callApi('getMany', { resource, ...params }),
	getManyReference: (resource, params) => callApi('list', { resource, ...params, [params.target]: params.id }),
	create:     (resource, params) => {
		//Strip files object from query if set
		let { files, safeParams } = extractFiles(resource, params);

		return callApi('create', { resource, ...safeParams }).then(async (response) => {
			if(files.length > 0) {
				//Upload files after create query is done
				const results = await processFiles(files, response);

				//React admin will not fetch the record again, uploaded files are missing from the response
				return await callApi('get', { resource, id: response.data.id });
			} else {
				return response;
			}
		});
	},
	update:     (resource, params) => {
		//Strip files object from query if set
		let { files, safeParams } = extractFiles(resource, params);

		return callApi('update', { resource, ...safeParams }).then(async (response) => {
			if(files.length > 0) {
				//Upload files after create query is done
				const results = await processFiles(files, response);

				//React admin will not fetch the record again, uploaded files are missing from the response
				return await callApi('get', { resource, id: response.data.id });
			} else {
				return response;
			}
		});
	},
	updateMany: (resource, params) => callApi('updateMany', { resource, ...params }),
	delete:     (resource, params) => callApi('delete', { resource, ...params }),
	deleteMany: (resource, params) => callApi('deleteMany', { resource, ...params }),
};

export default dataProvider;